const libHosts = require('headlight-qrcode/source/Headlight-QRCode-Hosts.js');

/**
 * Escapes a value for use as HTML text or as an attribute value; null and undefined become ''.
 * @param {any} pValue
 * @return {string}
 */
function escapeHTML(pValue)
{
	if ((pValue === null) || (typeof(pValue) === 'undefined'))
	{
		return '';
	}
	return String(pValue).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

class MediaCardBuilder
{
	constructor(pOptions = {})
	{
		// Default options can be set here
		const defaultOptions = {
			cardLayouts: {
				flexible: { rows: -1, columns: 2 }, // default card layout
				full: { rows: 1, columns: 2 }, // full card layout
				half: { rows: 2, columns: 2 }, // half card layout
				quarter: { rows: 4, columns: 2 }, // quarter card layout,
				minimalist: { rows: 2, columns: 1 }, // minimalist card layout
			},
			target: 'MediaDetailCards-Container', // target container for the media cards
			landscapeMode: false, // whether to use landscape mode for PDF generation
			cardSize: 'flexible', // default card size
			cardTheme: 'default', // default card theme ('default' and 'minimalist')
			sortOrder: 'asc', // default sort order
			sortBy: 'DesignatedTime', // default sort field
			ordinalPosition: 'left', // default ordinal location (left or right)
			ordinalPrefix: 'Photo ', // default ordinal prefix
			ordinalSuffix: ' - ', // what comes after the ordinal (by default, is placed before the title)
			ordinalScope: 'local', // default ordinal scope ("global" can be used for all instances of cards across the form)
			titleAlignment: 'center', // title alignment (left, center, right)
			titlePosition: 'below', // title location (above, below)
			hideOrdinal: false, // hide ordinal option
			hideTitle: false, // hide title option
			hideId: false, // hide ID option
			hideCreator: false, // hide creator option
			hideCreateDate: false, // hide create date option
			hideDescription: false, // hide description option
			hideMiniMap: false, // hide mini map option
			hideStationOffset: false, // hide station and offset row option
			hideLabels: false, // hide labels option
			hideExternalLink: false, // hide external link option
			userRecords: [], // user records provided from an external look up (used to build the userNameMap)
			userNameMap: {}, // map of user IDs to user names for display (userRecords entries are added on top)
			isPrintView: false, // whether to format for print view (hides buttons and interactive elements)
			primaryGroupByLabelType: null, // the primary label type to group by (if grouping by label types, this determines which label type is used for the mini map and external link display in the standard card template)
			secondaryGroupByLabelType: null, // the secondary label type to group by (if grouping by multiple label types, this determines which label type is used for the mini map and external link display in the standard card template when the primary label type is not available)
			disablePrimaryGroupByLabelTypePageBreaks: false, // whether to disable page breaks between primary groups when grouping by label types (only applies to PDF generation)
		};
		// Merge default options with any provided options
		this.options = { ...defaultOptions, ...pOptions };

		// DRY CSS classes to reduce tempalate duplication
		this.sharedLabelCSS = 'title is-7 has-text-text-50'; // default label classes for styling
		this.sharedDatumCSS = 'subtitle is-7 has-text-text'; // default datum classes for styling
		this.hideMeCSS = 'style="display: none;"'; // Inline style to hide elements (avoid css classes for simplicity)
		this.customCSSInjected = false; // flag to track whether component specific CSS has been injected into the document head
	}

	/**
	 * Inject CSS styles for the media cards into the document head, using options to set dynamic values such as card and image heights based on the PDF page height and landscape mode setting
	 * @param {Object} tmpOptions - the options to use for setting dynamic CSS values
	 * @return {void}
	 */
	injectCCSS(tmpOptions = {})
	{
		// Only inject CSS once, even if buildCards is called multiple times
		if (this.customCSSInjected || document.getElementById('MediaCardBuilder-Styles')) return;

		const styleElement = document.createElement('style');
		styleElement.id = 'MediaCardBuilder-Styles';
		styleElement.innerHTML = /*CSS*/`
			:root {
				--pdf-page-height: ${tmpOptions.landscapeMode ? 700: 950}px; /* Default PDF page height */
			}
			.media-card-wrapper .media-card-image {
				width: auto;
				object-fit: contain;
				margin: 0 auto;
				max-height: calc(var(--pdf-page-height) - 2rem);
			}
			.media-card-wrapper .media-card-description-text {
				line-height: normal;
			}
			/* Card height is a fixed height based on the PDF page height divided by the number of rows */
			.card-height-is-1-out-of-1 {
				height: calc(var(--pdf-page-height) / 1);
			}
			.card-height-is-1-out-of-2 {
				height: calc(var(--pdf-page-height) / 2);
			}
			.card-height-is-1-out-of-4 {
				height: calc(var(--pdf-page-height) / 4);
			}
			/* Image height is a max-height to allow for responsive images */
			.image-height-is-1-out-of-1 {
				max-height: calc(var(--pdf-page-height) / 1);
			}
			.image-height-is-1-out-of-2 {
				max-height: calc(var(--pdf-page-height) / 2);
			}
			.image-height-is-1-out-of-4 {
				max-height: calc(var(--pdf-page-height) / 4);
			}
			/* Card heights have to be tweaked for minimalist theme card sizes */
			.media-card-wrapper.media-card-theme_minimalist .card-height-is-1-out-of-1 {
				height: calc((var(--pdf-page-height) / 1) - 2rem);
			}
			.media-card-wrapper.media-card-theme_minimalist .card-height-is-1-out-of-2 {
				height: calc((var(--pdf-page-height) / 2) - 2rem);
			}
			.media-card-wrapper.media-card-theme_minimalist .card-height-is-1-out-of-4 {
				height: calc((var(--pdf-page-height) / 4) - 2rem);
			}
			.media-card-wrapper.media-card-theme_minimalist .image-height-is-1-out-of-1 {
				height: calc((var(--pdf-page-height) / 1) - ${tmpOptions.hideDescription ? 4 : 6 }rem);
			}
			.media-card-wrapper.media-card-theme_minimalist .image-height-is-1-out-of-2 {
				height: calc((var(--pdf-page-height) / 2) - ${tmpOptions.hideDescription ? 4 : 6 }rem);
			}
			.media-card-wrapper.media-card-theme_minimalist .image-height-is-1-out-of-4 {
				height: calc((var(--pdf-page-height) / 4) - ${tmpOptions.hideDescription ? 4 : 6 }rem);
			}`;
		document.head.appendChild(styleElement);
		this.customCSSInjected = true;
	}

	/**
	 * Build the media cards for the given observations and options
	 * @param {Array} pObservations - the observations to build the media cards for
	 * @param {Object} [pOptions] - the options to use for building the media cards (optional)
	 * @return {boolean}
	 */
	buildCards(pObservations = [], pOptions = {})
	{
		const tmpOptions = { ...this.options, ...pOptions };
		this.injectCCSS(tmpOptions);
		tmpOptions.userNameMap = this.buildUserNameMap(tmpOptions);
		let cardListTemplate = '';
		const mediaList = this.curateMediaObservationList(pObservations, tmpOptions);
		// if we have primary or secondary grouping by label types, we want to add dividers between the groups in the UI, so we need to build the HTML for the cards in a way that allows us to insert those dividers
		if ((tmpOptions.primaryGroupByLabelType || tmpOptions.secondaryGroupByLabelType) && !tmpOptions.hideGroupByLabelTypeHeaders)
		{
			// we need to keep track of the current group values as we loop through the media list to know when to insert dividers
			let currentPrimaryGroup = null;
			let currentSecondaryGroup = null;
			cardListTemplate = mediaList.map((mediaData, index) =>
			{
				// force page breaks for primary labels to ensure groups stay together in the PDF view
				let pageBreakClass = index !== 0 ? 'has-page-break-before' : '';
				let dividerTemplate = '';
				// if we have a new primary group, we need to insert a divider for the new primary group
				if (tmpOptions.primaryGroupByLabelType && mediaData._PrimaryGroupByLabel !== currentPrimaryGroup)
				{
					currentPrimaryGroup = mediaData._PrimaryGroupByLabel;
					dividerTemplate += /*HTML*/`
					<div class="cell is-col-span-full ${ pOptions?.disablePrimaryGroupByLabelTypePageBreaks ? '' : pageBreakClass}">
						<div class="notification is-primary mb-1 mt-1 p-2">
							<span class="icon">
								<i class="fa fa-fw fa-tag"></i>
							</span>
							<strong>${ tmpOptions.primaryGroupByLabelType }: ${ currentPrimaryGroup || '-None-' }</strong>
						</div>
					</div>`;
				}
				// if we have a new secondary group, we need to insert a divider for the new secondary group
				if (tmpOptions.secondaryGroupByLabelType)
				{
					const secondaryGroupValue = mediaData.Labels?.[tmpOptions.secondaryGroupByLabelType]?.[0]?.Tag || undefined;
					if (secondaryGroupValue !== currentSecondaryGroup)
					{
						currentSecondaryGroup = secondaryGroupValue;
						dividerTemplate += /*HTML*/`
						<div class="cell is-col-span-full">
							<div class="notification is-soft mb-1 mt-1 p-1">
								<span class="icon">
									<i class="fa fa-fw fa-tags"></i>
								</span>
								<strong>${ tmpOptions.secondaryGroupByLabelType }: ${ currentSecondaryGroup || '-None-' }</strong>
							</div>
						</div>`;
					}
				}
				// build the card template for the media data and add the divider template before it if necessary
				return dividerTemplate + this.buildCard(mediaData);
			}).join('');
		}
		else 
		{
			cardListTemplate = mediaList.map((mediaData) => this.buildCard(mediaData)).join('');
		}
		// insert the card list template into the target container in the DOM
		const tmpElementQuery = tmpOptions.target.startsWith('#') ? tmpOptions.target : `#${tmpOptions.target}`; // allow for target to be passed in with or without the # prefix, but default to using an ID selector for simplicity and specificity
		const containerElement = document.querySelector(tmpElementQuery);
		if (!containerElement)
		{
			console.warn(`MediaCardBuilder: Target container with ID "${tmpOptions.target}" not found.`);
			return false;
		}
		// If there are no media cards to display, show an empty message
		if (!cardListTemplate || !cardListTemplate.length)
		{
			containerElement.innerHTML =
			`<div class="notification has-text-centered empty-message">
				<span class="icon">
					<i class="fa fa-fw fa-info-circle"></i>
				</span>
				<span>No media has been added yet.</span>
			</div>`;
			return true;
		}
		containerElement.innerHTML = /*HTML*/`
		<div class="fixed-grid has-1-cols" style="wdith: 100%;">
			<div class="grid" style="gap: 0.25rem">${cardListTemplate}</div>
		</div>`;
		return true;
	}

	/**
	 * Map user IDs to display names: the caller's userNameMap, plus (overriding) any userRecords
	 * @param {Object} [pOptions] - the build options (userNameMap and/or userRecords)
	 * @return {Object} - map of IDUser to display name
	 */
	buildUserNameMap(pOptions = {})
	{
		return (pOptions.userRecords || []).reduce((acc, user) =>
		{
			acc[user.IDUser] = user.NameFirst + ' ' + user.NameLast;
			return acc;
		}, { ...pOptions.userNameMap });
	}

	/**
	 * Use a standard card template for the media card
	 * @param {Object} mediaData - the media data for the card
	 * @return {string} - the HTML string for the standard media card
	 */
	useStandardCardTemplate(mediaData)
	{
		let mediaElement;
		// If the media type is video or audio, adjust the media element accordingly
		if (mediaData._MediaType === 'video')
		{
			mediaElement = /*HTML*/`
			<figure data-observation-id="${mediaData.IDObservation}" style="padding: 1rem;">
				<video class="media-card-video ${mediaData._ImageHeightClass}" controls="" style="width: 100%;" playsinline>
					<source src="/1.0/Artifact/Media/${ mediaData._ArtifactId }/${ mediaData._Version }" type="video/mp4">
				</video>
			</figure>`;
		}
		else if (mediaData._MediaType === 'audio')
		{
			mediaElement = /*HTML*/`
			<figure data-observation-id="${mediaData.IDObservation}" style="padding: 1rem;">
				<audio class="media-card-audio ${mediaData._ImageHeightClass}" controls="" style="width: 100%;" playsinline>
					<source src="/1.0/Artifact/Media/${ mediaData._ArtifactId }/${ mediaData._Version }" type="audio/mp3">
				</audio>
			</figure>`;
		}
		else // default to image (all observations have a fallback image)
		{
			mediaElement = /*HTML*/`
			<figure data-observation-id="${mediaData.IDObservation}">
				<img class="image media-card-image ${mediaData._ImageHeightClass}"
					src="${mediaData._ImageSource || ''}" 
					alt="Observation #${mediaData.IDObservation}" 
					onerror="this.onerror = null; this.src='./images/no-image-light.png'"
				/>
			</figure>`;
		}
		return /*HTML*/`
		<!-- Standard card template for media cards -->
		<div class="cell" style="break-inside: avoid;">
			<div class="fixed-grid media-card-wrapper ${mediaData._CardColumnClass} media-card-theme_standard">
				<a class="anchor" id="MEDIA-CARD-FOR-${mediaData.IDObservation}"></a>
				<div class="grid is-gap-0 is-clipped ${mediaData._CardHeightClass}">
					<div class="cell has-background-text-soft p-0">
							${mediaElement}
						</figure>
					</div>
					<div class="cell pb-3">
						<div class="fixed-grid has-1-cols mb-3">
							<div class="grid has-background-text-soft" style="gap: 0.5rem;">
								<div class="cell">
									<p ${ mediaData._HideTitle ? this.hideMeCSS : ''} class="title is-7 mt-2 ml-2">${escapeHTML(mediaData.Name)}</p>
									<p class="${this.sharedDatumCSS} mb-2 ml-2">
										<span ${ mediaData._HideId ? this.hideMeCSS : ''}>#${mediaData.IDObservation || ''}</span>
										<a ${ mediaData._HideExternalLink ? this.hideMeCSS : ''} class="is-size-7 is-pulled-right" href="${mediaData._ExternalLinkHref || ''}" target="_blank">
											<span>Open in Fieldbook</span>
										</a>
									</p>
								</div>
								<div ${ mediaData._HideMiniMap ? this.hideMeCSS : ''} class="cell is-col-from-end-1 p-0 has-background-text-85" style="align-content: center;">
									<figure class="image mini-map-image">
										<img style="width: auto; float: right" onerror="this.onerror = null; this.src='./images/no-location-data-light.png'" src="${mediaData._MiniMapImage || ''}" />
									</figure>
								</div>
							</div>
						</div>
						<div class="fixed-grid has-2-cols">
							<div class="grid pl-3 pb-3 mb-0">
								<div ${ mediaData._HideCreator ? this.hideMeCSS : ''} class="cell">
									<div class="${this.sharedLabelCSS}">Created by</div>
									<div class="${this.sharedDatumCSS} media-card-creator-name" data-creator-id="${mediaData.CreatingIDUser || ''}">${mediaData._CreatorName || 'N/A'}</div>
								</div>
								<div ${ mediaData._HideCreateDate ? this.hideMeCSS : ''} class="cell">
									<div class="${this.sharedLabelCSS}">Created on</div>
									<div class="${this.sharedDatumCSS}">${new Date(mediaData.DesignatedTime).toLocaleDateString()} at ${new Date(mediaData.DesignatedTime).toLocaleTimeString()}</div>
								</div>
								<div ${ mediaData._HideDescription ? this.hideMeCSS : ''} class="cell is-col-span-2">
									<div class="${this.sharedLabelCSS} media-card-description-text">Description</div>
									<div class="${this.sharedDatumCSS}" style="display: inline-flex; flex-wrap: wrap;">
										${escapeHTML(mediaData.Description) || '<span class="has-text-text-50">No description provided</span>'}
									</div>
								</div>
								<div ${ mediaData._HideStationOffset || !mediaData._HasStationOffsetRow ? this.hideMeCSS : ''} class="cell">
									<div class="${this.sharedLabelCSS}">Station</div>
									<div class="${this.sharedDatumCSS}">${escapeHTML(mediaData.AlignmentStation) || 'N/A'}</div>
								</div>
								<div ${ mediaData._HideStationOffset || !mediaData._HasStationOffsetRow ? this.hideMeCSS : ''} class="cell">
									<div class="${this.sharedLabelCSS}">Offset</div>
									<div class="${this.sharedDatumCSS}">${escapeHTML(mediaData.AlignmentOffset) || 'N/A'}</div>
								</div>
								${ this.buildLabelSection(mediaData) }
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>`;
	}
	
	/**
	 * Use a minimalist card layout for the media card
	 * @param {Object} mediaData - the media data for the card
	 * @return {string} - the HTML string for the minimalist media card
	 */
	useMinimalistCardTemplate(mediaData)
	{
		// Build the ordinal and it's wrapping content for the minimalist card - will be "left" or "right" positions
		const buildOrdinal = () => 
		{
			return /*HTML*/`
				<span ${ mediaData._HideOrdinal ? this.hideMeCSS : ''} class="media-card-ordinal-prefix">
					${mediaData._OrdinalPrefix || ''}
				</span>
				<span ${ mediaData._HideOrdinal || mediaData._HideGlobalOrdinal ? this.hideMeCSS : ''} class="media-card-global-ordinal">
					${mediaData._GlobalOrdinal || ''}
				</span>
				<span ${ mediaData._HideOrdinal || mediaData._HideLocalOrdinal ? this.hideMeCSS : ''} class="media-card-local-ordinal">
					${mediaData.Ordinal || ''}
				</span>
				<span ${ mediaData._HideOrdinal ? this.hideMeCSS : ''} class="media-card-ordinal-suffix">
					${mediaData._OrdinalSuffix || ''}
				</span>
			`;
		}
		// Build the header for the minimalist card which can be in two different positions - "above" or "below" the image
		const buildHeader = () => 
		{
			return /*HTML*/`
				<div class="is-flex title is-7 mt-1 mb-0 ${ tmpAlignment }">
					<div class="is-flex-grow-1">
						${ mediaData._OrdinalPosition === 'left' ? buildOrdinal() : '' }
						<span ${ mediaData._HideTitle ? this.hideMeCSS : ''} class="media-card-title">
							${escapeHTML(mediaData.Name)}
						</span>
						${ mediaData._OrdinalPosition !== 'left' ? buildOrdinal() : '' }
					</div>
				</div>
			`;
		}

		// Translate the title/description alignment to a CSS class
		let tmpAlignment = '';
		switch(mediaData?._TitleAlignment?.toLowerCase()) {
			case 'left':
				tmpAlignment =  'has-text-left';
				break;
			case 'right':
				tmpAlignment =  'has-text-right';
				break;
			default:
				tmpAlignment =  'has-text-centered';
		}

		return /*HTML*/`
		<!-- Minimalist card template for media cards -->
		<div class="cell" style="break-inside: avoid;">
			<div class="fixed-grid media-card-wrapper has-1-cols media-card-theme_minimalist">
				<a class="anchor" id="MEDIA-CARD-FOR-${mediaData.IDObservation}"></a>
				<div class="grid is-gap-0 is-clipped mt-2 ${mediaData._CardHeightClass}">
					<div class="cell">
						${ mediaData._TitlePosition == 'above' ? buildHeader() : '' }
						<figure data-observation-id="${mediaData.IDObservation}">
							<img class="image media-card-image ${mediaData._ImageHeightClass}"
								src="${mediaData._ImageSource || ''}" 
								alt="Observation #${mediaData.IDObservation}" 
								onerror="this.onerror = null; this.src='./images/no-image-light.png'"
							/>
						</figure>
						${ mediaData._TitlePosition !== 'above' ? buildHeader() : '' }
						<div ${ mediaData._HideDescription ? this.hideMeCSS : ''} class="media-card-description-text is-size-7 ${tmpAlignment}">${escapeHTML(mediaData._TrimmedDescription)}</div>
					</div>
				</div>
			</div>
		</div>`;
	}

	/**
	 * Build a single media card HTML structure based on the card size and media data
	 * @param {Object} mediaData - the media data for the card
	 * @return {string} - the HTML string for the media card
	 */
	buildCard(mediaData)
	{
		switch (mediaData._CardTheme) {
			case 'minimalist':
				return this.useMinimalistCardTemplate(mediaData);
			default:
				return this.useStandardCardTemplate(mediaData);
		}
	}

	/**
	 * Build the labels section for the media card
	 * @param {Object} mediaData - the media data containing labels
	 * @return {string} - the HTML string for the labels section
	 */
	buildLabelSection(mediaData)
	{
		// Build the HTML for the labels section
		if (!mediaData._HasLabelsToDisplay)
		{
			return '';
		}
		let returnHtml = '';
		const labelGroups = mediaData._LabelGroups || [];
		// If there are no label groups, return an empty string
		if (!labelGroups || !labelGroups.length)
		{
			return '';
		}
		// Map through the label groups to create the HTML for each group
		const labelHtml = labelGroups.map((group) =>
		{
			// For each group, build the HTML for the labels
			let tagGroupHtml = '';
			const valuesHtml = group.Values.map((value) => /*HTML*/`<span class="is-size-7">${escapeHTML(value.Value)}</span>`).join(', ');
			// Create the HTML for the group with its labels
			tagGroupHtml = /*HTML*/`
			<div ${ mediaData._HideLabels ? this.hideMeCSS : ''} class="column">
				<div class="${this.sharedLabelCSS}">${escapeHTML(group.Key)}</div>
				<div class="${this.sharedDatumCSS}">${valuesHtml}</div>
			</div>`;
			return tagGroupHtml;
		}).join('');
		// Wrap the labels in a fixed grid container
		returnHtml = /*HTML*/`
		<div class="cell is-col-span-2">
			<div class="columns is-multiline">
				${labelHtml}
			</div>
		</div>`;
		return returnHtml;
	}

	/**
	 * Build the mini map image URL for the observation
	 * @param {Object} pObservation - the observation data to build the mini map image
	 * @return {string} - the URL for the mini map image or 'null'
	 */
	buildMiniMapImage(pObservation)
	{
		let hasValidCaptureGeoLocation = false;
		let hasValidUserGeoLocation = false;
		// check if the observation has a valid capture or user geolocation
		if (pObservation?.CaptureLatitude && pObservation?.CaptureLongitude)
		{
			hasValidCaptureGeoLocation = this.isValidLatitude(pObservation?.CaptureLatitude) &&
			this.isValidLongitude(pObservation?.CaptureLongitude) &&
			(Number(pObservation?.CaptureLatitude) !== 0 && Number(pObservation?.CaptureLongitude) !== 0);
		}
		if (pObservation?.UserLatitude && pObservation?.UserLongitude)
		{
			hasValidUserGeoLocation = this.isValidLatitude(pObservation?.UserLatitude) && this.isValidLongitude(pObservation?.UserLongitude) && (Number(pObservation?.UserLatitude) !== 0 && Number(pObservation?.UserLongitude) !== 0);
		}
		return hasValidCaptureGeoLocation || hasValidUserGeoLocation ? `/1.0/Observation/Map/For/${pObservation.IDObservation}` : `null`;
	}

	/**
	 * Trim the text to a maximum length and add ellipsis if it exceeds the length
	 * @param {string} text - the text to trim
	 * @param {number} maxLength - the maximum length of the text
	 * @return {string} - the trimmed text with ellipsis if it exceeds the maxLength
	 */
	trimText(text, maxLength = 500)
	{
		if (!text || typeof text !== 'string' || text.length <= maxLength)
		{
			return text;
		}
		return text.substring(0, maxLength) + '...';
	}

	/**
	 * Curate a list of observations into renderable media card data. Supports Image, Video,
	 * Audio, and File observation types. File observations (PDFs, documents, etc.) are rendered
	 * as thumbnail cards using the standard observation image endpoint.
	 * @param {Array} pObservations - the observations to build media cards for
	 * @param {Object} [pOptions] - the options to use for filtering and formatting the observations (optional)
	 * @return {Array} - the curated observations ready for card rendering
	 */
	curateMediaObservationList(pObservations, pOptions)
	{
		if (!pObservations || !pObservations.length)
		{
			return [];
		}
		// filter out observations that are not images
		let tmpObservations = pObservations.reduce((pFiltered, pObs) =>
		{
			let isMediaType = false;
			// determine if the observation has an image, video, or audio artifact
			if (pObs?.Details?.Video?.[0]?.ArtifactType?.toLowerCase() === 'video')
			{
				isMediaType = true;
				pObs._ArtifactId = pObs.Details.Video[0].IDArtifact; // set the artifact ID for the video
				pObs._MediaType = !pOptions.isPrintView ? 'video' : 'image'; // if in print view, treat video as image
				pObs._Version = pObs.Details.Video[0].Version;
			} 
			if (pObs?.Details?.Audio?.[0]?.ArtifactType?.toLowerCase() === 'audio')
			{
				isMediaType = true;
				pObs._ArtifactId = pObs.Details.Audio[0].IDArtifact; // set the artifact ID for the audio
				pObs._MediaType = !pOptions.isPrintView ? 'audio' : 'image'; // if in print view, treat audio as image
				pObs._Version = pObs.Details.Audio[0].Version;
			}
			if (pObs?.Details?.Image?.[0]?.ArtifactType?.toLowerCase() === 'image')
			{
				isMediaType = true;
				pObs._ArtifactId = pObs.Details.Image[0].IDArtifact; // set the artifact ID for the image
				pObs._MediaType = 'image';
				pObs._Version = pObs.Details.Image[0].Version;
			}
			// File observations (PDFs, documents, etc.) — render as thumbnail cards using the
			// standard observation image endpoint which returns a server-generated preview image.
			// Only matched when no higher-priority media type (Video/Audio/Image) was detected,
			// so an observation that has both an Image and a File detail won't be double-counted.
			if (!isMediaType && pObs?.Details?.File?.[0])
			{
				isMediaType = true;
				pObs._ArtifactId = pObs.Details.File[0].IDArtifact;
				pObs._MediaType = 'image'; // always use the thumbnail representation for file types
				pObs._Version = pObs.Details.File[0].Version;
			}
			if (isMediaType)
			{
				pObs._CardSize = pOptions?.cardSize; // set the card size based on options
				pObs._CardTheme = pOptions?.cardTheme; // set the card theme based on options
				// process the extra image details for the isolated observation card view
				pObs._ImageSource = `/1.0/Observation/${ pObs.IDObservation }/Image/Standard`;
				// flags passed from the configuration options
				pObs._HideTitle = pOptions?.hideTitle;
				pObs._HideId = pOptions?.hideId;
				pObs._HideCreator = pOptions?.hideCreator;
				pObs._HideCreateDate = pOptions?.hideCreateDate;
				pObs._HideDescription = pOptions?.hideDescription;
				// if the description is not hidden, we need to trim it to a maximum length
				if (!pObs?._HideDescription && pObs?.Description)
				{
					pObs._TrimmedDescription = this.trimText(pObs.Description, 425);
				}
				pObs._HideMiniMap = pOptions?.hideMiniMap;
				// build the mini map image URL based on the observation data
				if (!pObs?._HideMiniMap)
				{
					pObs._MiniMapImage = this.buildMiniMapImage(pObs);
				}
				// look up the user name from the map we built earlier
				pObs._CreatorName = escapeHTML(pOptions?.userNameMap?.[pObs.CreatingIDUser]) || '<span class="has-text-text-50">Name not provided</span>';
				// set the column class for the card based on the template options
				pObs._CardColumnClass = `has-${ pOptions?.cardLayouts?.[pOptions?.cardSize]?.columns || 2 }-cols`;
				// set the row class for the card based on the template options
				const tmpRowsCount = pOptions?.cardLayouts?.[pOptions?.cardSize]?.rows;
				pObs._CardHeightClass = tmpRowsCount > 0 ? `card-height-is-1-out-of-${ tmpRowsCount }` : '';
				pObs._ImageHeightClass = tmpRowsCount > 0 ? `image-height-is-1-out-of-${ tmpRowsCount }` : '';
				// show the station and offset row if the configuration option is set and the values are not empty
				if (!pOptions?.hideStationOffset)
				{
					pObs._HideStationOffset = pOptions?.hideStationOffset;
					pObs._HasStationOffsetRow = (pObs?.AlignmentStation?.length && Number(pObs?.AlignmentStation) !== 0) || (pObs?.AlignmentOffset?.length && Number(pObs?.AlignmentOffset) !== 0) ? 1 : 0;
				}
				// show the labels for the observation if the configuration option is set
				if (!pOptions?.hideLabels)
				{
					// show the labels if they are available (if we have keys other than "SAVE" in the Labels object)
					pObs._HasLabelsToDisplay = Object.keys(pObs?.Labels).filter((key) => key !== 'SAVE').length > 0 ? 1 : 0;
					// if we have labels, we need to process them into a format that can be used in the view
					if (pObs._HasLabelsToDisplay)
					{
						const tmpLabelList = {};
						// loop through the labels and add them to the label list
						Object.entries(pObs.Labels || {}).forEach(([ labelTypeKey, labelObjects ]) =>
						{
							// skip the SAVE key as it is not a label we want to display
							if (labelTypeKey === 'SAVE')
							{
								return;
							}
							// if the value is not an array, we need to convert it to an array
							if (!Array.isArray(labelObjects))
							{
								labelObjects = [ labelObjects ];
							}
							// if the label type is not in the list, we need to add it
							if (!tmpLabelList[labelTypeKey])
							{
								tmpLabelList[labelTypeKey] = [];
							}
							for (const labelObject of labelObjects)
							{
								// if the label object has a value, we need to add it to the list
								if (labelObject?.Tag && labelObject.Tag.length)
								{
									tmpLabelList[labelTypeKey].push({
										Value: labelObject.Tag,
									});
								}
							}
						});
						// convert the label list to an array of objects with Key and Values properties
						pObs._LabelGroups = tmpLabelList ? Object.entries(tmpLabelList).map(([ key, values ]) => ({ Key: key, Values: values })) : [];
					}
				}
				// if the configuration option is set, we need show the external link to the observation in Fieldbook
				pObs._HideExternalLink = pOptions?.hideExternalLink;
				pObs._ExternalLinkHref = `${ this.buildLinksUrl() }/#/project/${ pObs.IDProject }/journal/list/observation/${ pObs.IDObservation }`;

				// NOTE: These config options below are limited to the "minimalist" card type for now
				pObs._HideOrdinal = pOptions?.hideOrdinal;
				pObs._HideLocalOrdinal = pOptions?.ordinalScope === 'global';
				pObs._HideGlobalOrdinal = pOptions?.ordinalScope !== 'global';
				pObs._OrdinalPrefix = pOptions?.ordinalPrefix || '';
				pObs._OrdinalSuffix = pOptions?.ordinalSuffix || '';
				pObs._OrdinalPosition = pOptions?.ordinalPosition || 'left';
				pObs._TitlePosition = pOptions?.titlePosition || 'below';
				pObs._TitleAlignment = pOptions?.titleAlignment || 'left';

				// add the observation to the filtered list
				pFiltered.push(pObs);
			}
			return pFiltered;
		}, []);
		// primary grouping (ex: Elements)
		if (pOptions?.primaryGroupByLabelType)
		{
			// set the primary label type flag on each observation for use in the card template
			tmpObservations.forEach((obs) => {
				const primaryLabel = obs.Labels?.[pOptions.primaryGroupByLabelType]?.[0];
				obs._PrimaryGroupByLabel = primaryLabel ? primaryLabel.Tag : '-None-';
				// if we have a secondary grouping, set the secondary label type flag on each observation for use in the card template
				if (pOptions?.secondaryGroupByLabelType)
				{
					const secondaryLabel = obs.Labels?.[pOptions.secondaryGroupByLabelType]?.[0];
					obs._SecondaryGroupByLabel = secondaryLabel ? secondaryLabel.Tag : '-None-';
				}
			});
			// if we are grouping by a label type, we want to sort the observations by that label first so that they are grouped together in the UI
			// we want to put '-None-' groups at the end, so we use localeCompare which will sort them after all other values
			tmpObservations.sort((a, b) => {
			 	const aPrimary = a._PrimaryGroupByLabel;
			 	const bPrimary = b._PrimaryGroupByLabel;
				// if secondary grouping is enabled, we want to sort by the secondary label within the primary groups
				if (pOptions?.secondaryGroupByLabelType) {
					const aSecondary = a._SecondaryGroupByLabel;
					const bSecondary = b._SecondaryGroupByLabel;
					if (aPrimary === bPrimary) {
						if (aSecondary === bSecondary) {
							return 0;
						}
						return aSecondary.localeCompare(bSecondary);
					}
				}
			 	return aPrimary.localeCompare(bPrimary);
			});
			// if we have '-None-' groups, we want to move them to the end of the list after sorting so that they don't break up the groups with actual labels
			const noneGroupObservations = tmpObservations.filter(obs => obs._PrimaryGroupByLabel === '-None-');
			const otherGroupObservations = tmpObservations.filter(obs => obs._PrimaryGroupByLabel !== '-None-');
			tmpObservations = [ ...otherGroupObservations, ...noneGroupObservations ];
			
			// for clarification in the configuration, we will remove the other sort options since we are grouping by a label type to avoid confusion
			pOptions.sortBy = null;
			pOptions.sortOrder = null;
			return tmpObservations;
		}
		else {
			// sort the observations by the configured sort order and field
			return this.sortObservationsByKey(tmpObservations, pOptions?.sortBy, pOptions?.sortOrder);
		}
	}

	/**
	 * This method is used to validate if a passed string is a valid latitude geocoordinate.
	 * The value is not altered, but it throws out a warning in the console for invalid values.
	 * @param {string} value - the geolocation string
	 * @return {string} - the same, unaltered geolocation string
	 * @example
	 * IsValidLatitude('24.034345') => '24.034345'
	 */
	isValidLatitude = (value) =>
	{
		const latReg = RegExp(/^(\+|-)?(?:90(?:(?:\.0{0,16})?)|(?:[0-9]|[1-8][0-9])(?:(?:\.[0-9]{0,16})?))$/);
		const pass = latReg.test(value);
		if (!pass)
		{
			console.warn(`[Utility] This is not a valid Latitude coordinate:`, value);
			return '';
		}
		return value;
	};

	/**
	 * This method is used to validate if a passed string is a valid longitude geocoordinate.
	 * The value is not altered, but it throws out a warning in the console for invalid values.
	 * @param {string} value - the geolocation string
	 * @return {string} - the same, unaltered geolocation string
	 * @example
	 * IsValidLongitude('24.034345') => '24.034345'
	 */
	isValidLongitude = (value) =>
	{
		const longReg = RegExp(/^(\+|-)?(?:180(?:(?:\.0{0,16})?)|(?:[0-9]|[1-9][0-9]|1[0-7][0-9])(?:(?:\.[0-9]{0,16})?))$/);
		const pass = longReg.test(value);
		if (!pass)
		{
			console.warn(`[Utility] This is not a valid Longitude coordinate:`, value);
			return '';
		}
		return value;
	};

	/**
	 * The links URL for the page's environment, from the shared host rule in headlight-qrcode.
	 * @return {string} - The links URL for the current environment.
	 */
	buildLinksUrl()
	{
		const tmpLocation = (typeof window !== 'undefined' && window.location) ? window.location : {};
		return libHosts.getLinksBaseURL(libHosts.getEnvironmentForPage({ Protocol: tmpLocation.protocol, Hostname: tmpLocation.hostname, ServerURL: (typeof window !== 'undefined') ? window._OfflineServerBaseURL : undefined }));
	}

	/**
	 * Sorts the observations by a given key and order
	 * @param {Array} pObservations - The observations to sort
	 * @param {String} pSortBy - The key to sort by
	 * @param {String} pSortOrder - The order to sort by (asc or desc)
	 * @return {Array} - The sorted observations
	 */
	sortObservationsByKey(pObservations, pSortBy, pSortOrder)
	{
		if (!pObservations || !pObservations.length)
		{
			return pObservations;
		}
		if (!pSortBy)
		{
			console.info('No sort key provided - returning unsorted observations.');
			return pObservations;
		}
		// TODO: Add support for Labels and Datums
		const tmpSortOrder = pSortOrder && pSortOrder.toLowerCase() === 'desc' ? -1 : 1;
		const tmpSortedObservations = pObservations.sort((a, b) =>
		{
			const aValue = a[pSortBy];
			const bValue = b[pSortBy];
			if (aValue < bValue)
			{
				return -1 * tmpSortOrder;
			}
			if (aValue > bValue)
			{
				return 1 * tmpSortOrder;
			}
			return 0;
		});
		return tmpSortedObservations;
	}

	/**
	 * This function handles the global ordinal numbering for media cards on the DOM after everything is loaded.
	 */
	handleGlobalOrdinalNumbering()
	{
		// scrape the UI and reapply global ordinals from top to bottom
		$('.media-card-wrapper .media-card-global-ordinal').each((index, element) =>
		{
			$(element).text(index + 1);
		});
	}
}

module.exports = MediaCardBuilder;
